/**
 * Locked, on-brand palette for user avatar colours.
 *
 * A user's avatar colour is chosen by an admin in Admin → Users and stored as a
 * palette *key* (e.g. "navy") on `users.avatarColor` / `CurrentUser.avatarColor`.
 * The same colour is then rendered for that user everywhere their avatar appears
 * (work-item log entries, technician cards, the topbar).
 *
 * All hex values come from the documented design-system tokens (see the
 * `sm4rt-web-ui` skill §1). Keep this list in sync with the backend's allowed-key
 * set in `api/users` (server-side lock-down).
 */

export interface AvatarColour {
  /** Stable key persisted on the user record. */
  key: string;
  /** Background colour. */
  bg: string;
  /** Foreground (text/initials) colour with adequate contrast on `bg`. */
  fg: string;
}

export const AVATAR_PALETTE: readonly AvatarColour[] = [
  { key: 'blue', bg: '#4A90D9', fg: '#ffffff' },
  { key: 'navy', bg: '#0C447C', fg: '#ffffff' },
  { key: 'teal', bg: '#1D9E75', fg: '#ffffff' },
  { key: 'purple', bg: '#9B59B6', fg: '#ffffff' },
  { key: 'orange', bg: '#E67E22', fg: '#ffffff' },
  { key: 'amber', bg: '#EF9F27', fg: '#412402' },
  { key: 'red', bg: '#E74C3C', fg: '#ffffff' },
  { key: 'slate', bg: '#7F8C8D', fg: '#ffffff' },
] as const;

/** Allowed palette keys — the locked-down selection set. */
export const AVATAR_COLOR_KEYS: readonly string[] = AVATAR_PALETTE.map((c) => c.key);

/**
 * Dedicated avatar colour for the automated "system" actor (orchestrator /
 * SM4RT-generated log entries and any system-owned work-item group). NOT part
 * of `AVATAR_PALETTE` — it isn't admin-selectable; it's reserved so system
 * activity always reads as a neutral dark grey wherever its avatar appears.
 */
export const SYSTEM_AVATAR_COLOR: AvatarColour = { key: 'system', bg: '#586069', fg: '#ffffff' };

/** Normalised identifiers that denote the automated system / SM4RT actor. */
const SYSTEM_USER_IDS: ReadonlySet<string> = new Set([
  'system',
  'sm4rt',
  'sm4rt system',
  'orchestrator',
]);

/** True when an avatar ref (by id or display name) denotes the system actor. */
export function isSystemUserRef(ref: { id?: string | null; name?: string | null }): boolean {
  const id = (ref.id || '').trim().toLowerCase();
  const name = (ref.name || '').trim().toLowerCase();
  return SYSTEM_USER_IDS.has(id) || SYSTEM_USER_IDS.has(name);
}

/** True when `value` is a valid palette key. */
export function isAvatarColorKey(value: string | null | undefined): boolean {
  return value != null && AVATAR_COLOR_KEYS.includes(value);
}

/** Deterministic palette pick from a stable seed — same colour every render. */
function hashToPalette(seed: string): AvatarColour {
  const hash = seed
    .split('')
    .reduce((acc, ch) => acc + ch.charCodeAt(0), 0);
  return AVATAR_PALETTE[hash % AVATAR_PALETTE.length];
}

/**
 * Resolve the avatar colour for a user.
 *
 * - If `storedColor` is a valid palette key, that colour wins (admin's choice).
 * - Otherwise fall back to a deterministic pick from `seed` (e.g. the user's id),
 *   so a user with no chosen colour still gets a stable, on-palette colour.
 *
 * Always returns a palette member — never undefined.
 */
export function resolveAvatarColor(opts: {
  storedColor?: string | null;
  seed: string;
}): AvatarColour {
  const { storedColor, seed } = opts;
  if (isAvatarColorKey(storedColor)) {
    return AVATAR_PALETTE.find((c) => c.key === storedColor)!;
  }
  return hashToPalette(seed || '');
}

/** Derive up to two uppercase initials from a display name. */
export function getInitials(name: string): string {
  const parts = (name || '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}
